import { useEffect, useRef, useState } from "react";
import { KeyRound, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const FEED = {
  path: "/admin/website-feed/key", id: "feed-key", title: "Website Feed Key", button: "Copy Website Feed Key",
  text: "Lets your company website read published ads (read-only).",
};
export const NEWSLETTER_KEY = {
  path: "/admin/website-newsletter/key", id: "newsletter-key", title: "Website Newsletter Write Key", button: "Copy Newsletter Write Key",
  text: "Lets the website's server add newsletter subscribers to Contacts. Separate from the feed key — server-side only, never in website frontend code.",
};

// The key is preloaded into memory (never rendered) so the tap can write it synchronously:
// iPhone Safari only allows clipboard writes directly inside the tap, and must not move focus or scroll.
export function WebsiteFeedKey({ cfg = FEED }) {
  const { user } = useAuth();
  const keyRef = useRef(null);
  const [state, setState] = useState("loading");
  const isSuper = user?.role === "super_admin";
  useEffect(() => {
    if (!isSuper) return;
    api.get(cfg.path).then((r) => { keyRef.current = r.data.key; setState(""); }).catch(() => setState("unavailable"));
    return () => { keyRef.current = null; };
  }, [isSuper, cfg.path]);
  if (!isSuper) return null;
  const copy = (e) => {
    e.preventDefault();
    const y = window.scrollY;
    const active = document.activeElement;
    const restore = () => {
      if (document.activeElement !== active && document.activeElement?.blur) document.activeElement.blur();
      if (window.scrollY !== y) window.scrollTo(0, y);
    };
    const done = (ok) => { restore(); setState(ok ? "done" : "error"); if (ok) setTimeout(() => setState(""), 2500); };
    if (!keyRef.current || !navigator.clipboard?.writeText) { done(false); return; }
    navigator.clipboard.writeText(keyRef.current).then(() => done(true), () => done(false));
    restore();
  };
  return (
    <section data-testid={`website-${cfg.id}`} className="rounded-xl border border-stone-200 bg-white p-5">
      <h2 className="font-semibold text-stone-900 mb-1">{cfg.title}</h2>
      <p className="text-xs text-stone-500 mb-4">
        {cfg.text} The key is copied to your clipboard without being shown.
        Paste it only into the website project's admin settings, never into chat or email.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" data-testid={`copy-${cfg.id}-button`} onPointerDown={(e) => e.preventDefault()} onMouseDown={(e) => e.preventDefault()} onClick={copy}
          disabled={state === "loading" || state === "unavailable"} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
          {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : state === "done" ? <><Check className="h-4 w-4 mr-1.5" />Copied</> : <><KeyRound className="h-4 w-4 mr-1.5" />{cfg.button}</>}
        </Button>
        <span role="status" aria-live="polite" data-testid={`copy-${cfg.id}-status`} className="text-xs font-medium">
          {state === "done" && <span className="text-emerald-700">Key copied</span>}
          {state === "error" && <span className="text-red-700">Couldn't copy — please tap again.</span>}
          {state === "unavailable" && <span className="text-red-700">Key isn't configured.</span>}
        </span>
      </div>
    </section>
  );
}
